/**
 * LocalStorage key for auth state change notifications.
 * Using localStorage events allows communication across separate windows (Office dialogs).
 *
 * Important: never persist OAuth tokens or full OIDC user payloads here — other tabs and
 * extensions can read localStorage; canonical tokens remain under `oidc.*` keys only.
 */
export const INFLO_AUTH_BROADCAST_STORAGE_KEY = 'inflo:auth-state-change';

function broadcastAuthDetail(detail: AuthEventDetail, sameWindowEventName: string): void {
  const safePayload: AuthEventDetail =
    detail.loginHint !== undefined
      ? { timestamp: detail.timestamp, eventType: detail.eventType, loginHint: detail.loginHint }
      : { timestamp: detail.timestamp, eventType: detail.eventType };
  localStorage.setItem(INFLO_AUTH_BROADCAST_STORAGE_KEY, JSON.stringify(safePayload));
  window.dispatchEvent(new CustomEvent<AuthEventDetail>(sameWindowEventName, { detail }));
  setTimeout(() => {
    localStorage.removeItem(INFLO_AUTH_BROADCAST_STORAGE_KEY);
  }, 100);
}

/**
 * Event detail types for authentication events (tokens live in OIDC storage only).
 */
export interface AuthEventDetail {
  timestamp: number;
  eventType: 'login' | 'logout' | 'state-changed' | 'reauth-required';
  /** Hint for IdP (e.g. UPN / email) when session must be re-established interactively */
  loginHint?: string;
}

/**
 * Dispatches a notification when user logs in
 * Uses localStorage events to work across separate windows (Office dialogs)
 */
export const dispatchUserLoggedIn = () => {
  broadcastAuthDetail(
    {
      timestamp: Date.now(),
      eventType: 'login',
    },
    'inflo:user-logged-in',
  );
};

/**
 * Dispatches a notification when user logs out
 * Uses localStorage events to work across separate windows (Office dialogs)
 */
export const dispatchUserLoggedOut = () => {
  broadcastAuthDetail(
    {
      timestamp: Date.now(),
      eventType: 'logout',
    },
    'inflo:user-logged-out',
  );
};

/**
 * Session can no longer be renewed silently (e.g. expired refresh token). Triggers interactive re-login.
 */
export const dispatchInteractiveReauthRequired = (loginHint?: string) => {
  broadcastAuthDetail(
    {
      timestamp: Date.now(),
      eventType: 'reauth-required',
      loginHint,
    },
    'inflo:interactive-reauth-required',
  );
};
